import type { CSSProperties } from 'react';
import type { TFunction } from 'i18next';
import { Tag } from 'antd';

export type TransportStatusView = 'all' | 'pending' | 'active' | 'archived' | 'custom';

export const TRANSPORT_STATUS_VIEW_GROUPS: Record<
  Exclude<TransportStatusView, 'custom' | 'all'>,
  string[]
> = {
  pending: ['PENDING', 'SCHEDULED'],
  active: ['LOADING', 'IN_TRANSIT'],
  archived: ['DELIVERED', 'CANCELLED'],
};

export function detectTransportStatusView(statusFilter: string[]): TransportStatusView {
  if (statusFilter.length === 0) return 'all';

  const sortedFilter = [...statusFilter].sort().join(',');

  for (const view of Object.keys(TRANSPORT_STATUS_VIEW_GROUPS) as Array<
    keyof typeof TRANSPORT_STATUS_VIEW_GROUPS
  >) {
    const groupKey = [...TRANSPORT_STATUS_VIEW_GROUPS[view]].sort().join(',');
    if (sortedFilter === groupKey) return view;
  }

  return 'custom';
}

// Single home for the "consolidated vs single-order" transport badge, used by
// both the transports list (route cell) and the transport detail page (card
// header). A transport carrying 2+ orders reads "Zbiorczy · N zam.", one order
// reads "Pojedynczy" — kept in one place so the two pages can never drift.
export function renderTransportTypeTag(
  t: TFunction,
  orderCount: number,
  style?: CSSProperties,
): React.ReactNode {
  if (orderCount >= 2) {
    return (
      <Tag color="blue" bordered={false} style={style}>
        {t('transport:type.consolidatedWithCount', { count: orderCount })}
      </Tag>
    );
  }

  return (
    <Tag color="default" bordered={false} style={style}>
      {t('transport:type.single')}
    </Tag>
  );
}

// MED (i18n error mapping): the backend's transport-mutation error messages
// are English (`error.message` in transportService.js) and are meant to stay
// that way for logs/API consumers — the FRONTEND is responsible for
// translating known, stable error CODES into Polish-first copy instead of
// echoing the raw string. Single home so create/edit/delete/split all map the
// same codes identically. Codes not covered here fall through to the
// caller's own generic fallback message.
export interface TransportMutationErrorResponse {
  code?: string;
  error?: string;
  message?: string;
  fields?: string[];
  wzDocuments?: Array<{ id?: string; wzNumber?: string; orderId?: string }>;
}

export function mapTransportMutationErrorToMessage(
  t: TFunction,
  data: TransportMutationErrorResponse | undefined | null,
): string | undefined {
  if (!data?.code) {
    return undefined;
  }

  if (data.code === 'MIXED_UNIT_QUANTITY_ALLOCATION') {
    return t('transport:allocation.mixedQuantityUnitsBlocked');
  }

  if (data.code === 'WZ_TRANSPORT_LINK_EXISTS') {
    const numbers = (data.wzDocuments || [])
      .map((wz) => wz.wzNumber)
      .filter(Boolean)
      .join(', ');
    return numbers
      ? t('transport:messages.wzLinkExistsWithNumbers', { numbers })
      : t('transport:messages.wzLinkExists');
  }

  if (data.code === 'INVALID_TRANSPORT_SPLIT') {
    return t('transport:split.invalidSplit');
  }

  if (data.code === 'ORDER_FINANCIAL_LIMIT_EXCEEDED') {
    return t('transport:messages.orderFinancialLimitExceeded');
  }

  return undefined;
}
